import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowUp, Sparkles, Zap, MessageCircle } from "lucide-react";
import PdfPreview from "./PdfPreview";

const EXAMPLES = [
  "Reforma residencial completa para a cliente Ana, R$ 18.500, início em 15 dias.",
  "Desenvolvimento de app mobile para a Loja Vibe: design R$ 6.000 e build R$ 12.000.",
  "Ensaio fotográfico de casamento, pacote completo por R$ 3.200, entrega em 20 dias.",
  "Consultoria de marketing digital, 3 meses, R$ 2.500/mês para a Padaria do Zé.",
  "Manutenção preventiva industrial, visita mensal R$ 1.800, contrato anual.",
];

function useTypewriter(phrases, active) {
  const [text, setText] = useState("");
  const idx = useRef(0);
  const pos = useRef(0);
  const deleting = useRef(false);
  useEffect(() => {
    if (!active) return;
    let t;
    const tick = () => {
      const full = phrases[idx.current % phrases.length];
      if (!deleting.current) {
        pos.current++;
        setText(full.slice(0, pos.current));
        if (pos.current >= full.length) {
          deleting.current = true;
          t = setTimeout(tick, 2200);
          return;
        }
        t = setTimeout(tick, 34);
      } else {
        pos.current--;
        setText(full.slice(0, pos.current));
        if (pos.current <= 0) {
          deleting.current = false;
          idx.current++;
          t = setTimeout(tick, 300);
          return;
        }
        t = setTimeout(tick, 16);
      }
    };
    t = setTimeout(tick, 600);
    return () => clearTimeout(t);
  }, [active, phrases]);
  return text;
}

export default function Hero({ onSubmit }) {
  const [text, setText] = useState("");
  const focused = text.length > 0;
  const typed = useTypewriter(EXAMPLES, !focused);

  const submit = () => onSubmit(text);
  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } };

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden px-5 pt-24 pb-16 text-center">
      {/* premium white + light-blue ambient */}
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[520px] rounded-full opacity-90 blur-3xl" style={{ background: "radial-gradient(ellipse at center, #EAE7FF 0%, rgba(244,245,255,0.55) 45%, transparent 72%)" }} />
      <div className="pointer-events-none absolute top-28 right-[10%] w-[380px] h-[380px] rounded-full opacity-50 blur-3xl" style={{ background: "radial-gradient(circle at center, rgba(74,59,250,0.16), transparent 70%)" }} />
      <div className="pointer-events-none absolute inset-0 opacity-[0.28]" style={{ backgroundImage: "radial-gradient(#4A3BFA 0.5px, transparent 0.5px)", backgroundSize: "26px 26px", maskImage: "radial-gradient(ellipse 65% 55% at 50% 42%, black, transparent)" }} />

      {/* decorative floating PDFs (far sides, keep focus on the box) */}
      <div className="pointer-events-none absolute inset-0 hidden xl:block">
        <motion.div className="absolute left-[4%] top-1/2 w-[196px] opacity-45" style={{ translateY: "-50%" }} animate={{ y: [0, -12, 0] }} transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}>
          <div style={{ transform: "rotate(-8deg)", filter: "blur(0.4px)" }}><PdfPreview brand="Estúdio Norte" title="Proposta de Reforma" client="Ana Ribeiro" accent="#4A3BFA" compact /></div>
        </motion.div>
        <motion.div className="absolute right-[4%] top-1/2 w-[196px] opacity-45" style={{ translateY: "-50%" }} animate={{ y: [0, 12, 0] }} transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}>
          <div style={{ transform: "rotate(8deg)", filter: "blur(0.4px)" }}><PdfPreview brand="Marcenaria Real" title="Móveis sob medida" client="Marcos A." accent="#b45309" compact /></div>
        </motion.div>
      </div>

      <div className="relative z-10 w-full max-w-3xl mx-auto flex flex-col items-center">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[12px] font-medium bg-white ring-1 ring-slate-200 shadow-sm" data-testid="hero-badge">
          <span className="w-1.5 h-1.5 rounded-full bg-[#4A3BFA]" /> Orçamentos com IA · para qualquer nicho
        </motion.div>

        <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
          Seu orçamento pronto <br className="hidden sm:block" /><span style={{ color: "#4A3BFA" }}>em segundos.</span>
        </motion.h1>

        <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
          className="mt-5 text-lg text-slate-600 max-w-xl leading-relaxed">
          De uma mensagem informal para um PDF profissional pronto para enviar. Sem planilhas, sem formatação — no app ou direto pelo WhatsApp.
        </motion.p>

        {/* prompt box — the star */}
        <motion.div initial={{ opacity: 0, y: 22, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.75, delay: 0.2, ease: [0.22, 1, 0.36, 1] }} className="mt-10 w-full max-w-2xl">
          <div className="text-[12px] font-medium text-slate-400 mb-2">O que você quer orçar?</div>
          <div className="relative">
            <div className="pointer-events-none absolute -inset-2 rounded-[2rem] bg-slate-300/25 blur-2xl" />
            <div className="relative bg-white rounded-3xl ring-1 ring-slate-200 shadow-[0_28px_70px_-24px_rgba(15,23,42,0.28)] focus-within:ring-slate-300 transition-all">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={onKey}
                rows={3}
                data-testid="hero-prompt-input"
                className="w-full resize-none bg-transparent outline-none px-6 py-5 pr-16 text-[16px] text-slate-800 leading-relaxed scrollbar-none text-left"
                placeholder=""
              />
              {!focused && (
                <div className="pointer-events-none absolute left-6 top-5 pr-16 text-[16px] text-slate-400 leading-relaxed text-left">
                  {typed}<span className="inline-block w-[2px] h-[17px] align-middle bg-slate-400 ml-0.5 animate-pulse" />
                </div>
              )}
              <button onClick={submit} data-testid="hero-submit-btn" className="absolute bottom-3.5 right-3.5 grid place-items-center w-11 h-11 rounded-full bg-slate-900 text-white hover:bg-slate-800 transition-all hover:scale-105 active:scale-95">
                <ArrowUp size={19} strokeWidth={2.4} />
              </button>
            </div>
          </div>

          <button onClick={submit} data-testid="hero-cta-btn" className="cta-bright mt-6 inline-flex items-center justify-center gap-2 h-14 px-9 rounded-full text-[16px] font-bold tracking-tight transition-transform hover:scale-[1.03] active:scale-95">
            <Sparkles size={19} /> Gerar meu orçamento grátis
          </button>

          <div className="mt-5 flex items-center gap-5 justify-center text-[12.5px] text-slate-400">
            <span className="inline-flex items-center gap-1.5"><Zap size={13} className="text-[#4A3BFA]" /> Primeiro orçamento grátis</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Sem cartão</span>
            <span className="hidden sm:inline-flex items-center gap-1.5"><MessageCircle size={13} className="text-[#25D366]" /> Funciona no WhatsApp</span>
          </div>
        </motion.div>

        {/* compact preview for mobile/tablet (hidden where side previews show) */}
        <div className="xl:hidden mx-auto max-w-[230px] mt-12">
          <PdfPreview brand="Estúdio Norte" title="Proposta de Reforma" client="Ana Ribeiro" accent="#4A3BFA" compact />
        </div>
      </div>
    </section>
  );
}
